import { Badge } from '@radix-ui/themes'
import { useDateFormatter } from '@react-aria/i18n'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import VoiceGradientReusable from 'components/Dashboard/SendCall/VoiceGradientReusable'
import { useAuth } from 'hooks/useAuth'
import { AlertTriangle, CheckCircle, ChevronDown, Clock, MoveDownRight, MoveUpRight, UserPlus, X, XCircle } from 'lucide-react'
import React, { useState } from 'react'
import {
  Button as AriaButton,
  Autocomplete,
  Input,
  ListBox,
  ListBoxItem,
  Popover,
  SearchField,
  Select,
  SelectValue,
  useFilter,
} from 'react-aria-components'
import { toast } from 'react-toastify'
import { $fetch, useFetch } from 'utils/fetch'
import { convertToMinutesAndSeconds } from 'utils/formatting/secsToMins.js'
import { parsePathwayTags } from 'utils/funcs/call-logs/parsePathwayTags'

export default function CallMetadata({ call }) {
  const queryClient = useQueryClient()
  // Remove tagsDirty state, only need pendingTags
  const [pendingTags, setPendingTags] = useState(new Set())
  const variables = call.variables ?? {}

  const consecutiveWaitingTwice = call.transcript?.some((entry, index) => {
    const currentEntry = entry.text.toLowerCase().includes('[waiting]')
    const nextEntry = call.transcript[index + 1]?.text.toLowerCase().includes('[waiting]')
    return currentEntry && nextEntry
  })

  const statusOptions = [
    { key: 'none', label: 'No Review Status', color: 'default', icon: XCircle },
    { key: 'pending', label: 'Pending Review', color: 'warning', icon: Clock },
    { key: 'in_progress', label: 'In Progress', color: 'primary', icon: Clock },
    { key: 'completed', label: 'Completed', color: 'success', icon: CheckCircle },
    { key: 'not_needed', label: 'Not Needed', color: 'default', icon: XCircle },
  ]
  const { contains } = useFilter({ sensitivity: 'base' })

  const { org } = useAuth()

  const { data: orgMembers = [] } = useFetch(`/orgs/${org?.id}/members`, {
    queryKey: ['orgMembers', org?.id],
    enabled: !!org?.id,
  })

  const qcStatus = call.qc_status || 'none'
  const assigneeId = call.qc_assignee
  const qcTags = call.qc_tags || []

  const assignee = orgMembers.find(member => member.user_id === assigneeId)

  // Get QC tags from org preferences
  const qcTagOptions = org?.preferences?.qc_tags || [{ label: 'Successful', type: 'success' }, { label: 'Technical Issue', type: 'failure' }, { label: 'Wrong Number', type: 'failure' }, { label: 'No Answer', type: 'failure' }, { label: 'Needs Review', type: 'neutral' }]
  // Update review status mutation with abort support for QC tags
  // Use a ref to store the lastAbortController, and call useMutation directly at the top level
  const lastAbortControllerRef = React.useRef(null)

  const updateStatus = useMutation({
    mutationFn: async (data) => {
      // If this is a QC tags update, abort any previous QC tags request
      if (data.qc_tags !== undefined) {
        if (lastAbortControllerRef.current) {
          lastAbortControllerRef.current.abort()
        }
        lastAbortControllerRef.current = new AbortController()
      }
      else {
        lastAbortControllerRef.current = null
      }

      // If qc_status is "none", replace it with null before sending to backend
      if (data.qc_status === 'none') {
        data.qc_status = null
      }

      return await $fetch(`/v1/qc/${call.c_id}/review`, {
        method: 'PATCH',
        body: data,
        signal: lastAbortControllerRef.current ? lastAbortControllerRef.current.signal : undefined,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['callDetails', call.c_id])
      toast.success('Review status updated')
    },
    onError: (error) => {
      // Only show error if not an abort
      if (error?.name !== 'AbortError') {
        toast.error('Failed to update review status')
      }
    },
  })

  // Assign call mutation
  const assignCall = useMutation({
    mutationFn: async (assigneeId) => {
      return await $fetch(`/v1/qc/${call.c_id}/assign`, {
        method: 'POST',
        body: { assignee_id: assigneeId },
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['callDetails', call.c_id])
    },
    onError: () => {
      toast.error('Failed to assign call')
    },
  })

  const currentStatus = statusOptions.find(s => s.key === qcStatus) || statusOptions[0]

  const { data: voice } = useQuery({
    queryKey: ['voice', call.c_id],
    queryFn: async () => {
      const { voice } = await $fetch(`/v1/calls/${call.c_id}/voice`, {
        retry: false,
      })
      return voice
    },
    enabled: !!call.c_id,
    staleTime: Infinity,
    retry: false,
  })

  const transferDuration = call.created_at && call.transferred_at
    ? call.call_length - ((new Date(call.transferred_at) - new Date(call.created_at)) / (1000 * 60))
    : null

  const formatter = useDateFormatter({ dateStyle: 'long', timeStyle: 'short' })

  // Sync pendingTags with qcTags when call.qc_tags changes
  React.useEffect(() => {
    setPendingTags(new Set(qcTags))
  }, [qcTags])

  const callFields = [
    {
      label: 'From',
      value: call.from,
    },
    {
      label: 'To',
      value: call.to,
    },
    {
      label: 'Created At',
      value: call.created_at ? formatter.format(new Date(call.created_at)) : '-',
    },
    {
      label: 'Total Call Duration',
      value: call.call_length
        ? convertToMinutesAndSeconds(call.call_length)
        : null,
    },
    ...(call.transferred_to
      ? [{
          label: 'Transfer Duration',
          value: transferDuration
            ? convertToMinutesAndSeconds(transferDuration)
            : '-',
        }, {
          label: 'Transferred To',
          value: call.transferred_to,
        }]
      : []),
    {
      label: 'Call Type',
      value: (
        <Badge size="1" color={call.inbound ? 'green' : 'blue'}>
          {call.inbound ? 'Incoming' : 'Outgoing'}
          {call.inbound
            ? (
                <MoveDownRight size={15} color="#4CAF50" />
              )
            : (
                <MoveUpRight size={15} color="#2196F3" />
              )}
        </Badge>
      ),
    },
    {
      label: 'Cost',
      value: `$${call.price ? Number.parseFloat(call.price).toFixed(2) : '0.00'}`,
    },
    {
      label: 'Caller Location',
      value: variables.state
        ? `${variables.city ?? '-'}, ${variables.state}`
        : null,
    },
    {
      label: 'Language',
      value: variables.language,
    },
    {
      label: 'Answered By',
      value: call.answered_by,
    },
    {
      label: 'Ended By',
      value: call.call_ended_by,
    },
    {
      label: 'Status',
      value: call.status,
    },
    {
      label: 'Pathway Name',
      value: call.pathway_name
        ? (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault()
                window.open(
                  `${window.location.origin}/dashboard/convo-pathways?id=${call.pathway_id}`,
                  '_blank',
                )
              }}
              disabled={!call.pathway_id}
              className={`
                col-span-2 max-w-full truncate text-xs
                disabled:no-underline disabled:hover:no-underline
              `}
              style={{
                cursor: call.pathway_id ? 'pointer' : 'default',
                textDecoration: call.pathway_id ? 'underline' : 'none',
              }}
            >
              {call.pathway_name || '-'}
            </button>
          )
        : null,
    },
    {
      label: 'Voice',
      value: voice
        ? (
            <div className="flex items-center gap-1.5">
              <VoiceGradientReusable name={voice.id} size={12} />
              <p className="text-xs font-semibold tracking-tight text-gray-500">
                {voice.name}
              </p>
            </div>
          )
        : null,
    },
    {
      label: 'Review Assignee',
      value: (
        <div className="flex items-center gap-1.5">
          {assignee
            ? (
                <div className="flex items-center gap-1.5">
                  <Badge
                    size="1"
                    color="blue"
                    className="flex items-center gap-1.5"
                  >
                    {assignee.first_name}
                    {' '}
                    {assignee.last_name}
                    <button
                      type="button"
                      aria-label="Unassign"
                      className={`
                        text-gray-400
                        hover:text-red-500
                        focus:outline-none
                      `}
                      onClick={() => assignCall.mutate(null)}
                      title="Unassign"
                      style={{ display: 'flex', alignItems: 'center', background: 'none', border: 'none', padding: 0 }}
                    >
                      <X size={14} />
                    </button>
                  </Badge>
                </div>
              )
            : (
                <Select
                  items={orgMembers}
                  onSelectionChange={key => key && assignCall.mutate(key)}
                >
                  <AriaButton className={`
                    flex items-center justify-between rounded-md border px-3
                    py-1
                  `}
                  >
                    <SelectValue>
                      {() => (
                        <span className="flex items-center gap-2">
                          <UserPlus size={14} />
                          Assign
                        </span>
                      )}
                    </SelectValue>
                    <ChevronDown size={14} />
                  </AriaButton>
                  <Popover className={`
                    mt-1 flex max-h-80 min-w-[180px] flex-col rounded-md border
                    border-gray-200 bg-white shadow-lg
                  `}
                  >
                    <Autocomplete filter={contains}>
                      <SearchField
                        aria-label="Search members"
                        autoFocus
                        className={`
                          flex items-center border-b border-gray-200 px-3 py-2
                        `}
                      >
                        <Input
                          placeholder="Search members..."
                          className={`
                            flex-1 bg-transparent text-sm outline-none
                            placeholder:text-gray-400
                          `}
                        />
                      </SearchField>
                      <ListBox className="flex-1 overflow-auto p-1" items={orgMembers}>
                        {member => (
                          <ListBoxItem
                            id={member.user_id}
                            textValue={`${member.first_name} ${member.last_name}`}
                            className={`
                              flex cursor-pointer flex-col rounded px-2 py-1.5
                              text-sm outline-none
                              hover:bg-gray-100
                              focus:bg-gray-100
                            `}
                          >
                            <div className="flex items-center gap-2">
                              <UserPlus size={14} />
                              <span>
                                {member.first_name}
                                {' '}
                                {member.last_name}
                              </span>
                            </div>
                            {member.member_email && (
                              <span className="ml-6 text-xs text-gray-500">
                                {member.member_email}
                              </span>
                            )}
                          </ListBoxItem>
                        )}
                      </ListBox>
                    </Autocomplete>
                  </Popover>
                </Select>
              )}
        </div>
      ),
    },
    {
      label: 'Review Status',
      value: (
        <div className="flex min-w-[150px] flex-col items-end gap-2">
          <Select
            items={statusOptions}
            selectedKey={currentStatus.key}
            onSelectionChange={key => updateStatus.mutate({ qc_status: key })}
          >
            <AriaButton className={`
              flex min-w-[150px] items-center justify-between rounded-md border
              border-gray-300 bg-white px-2 py-1 text-xs outline-none
              hover:bg-gray-50
              focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20
              disabled:cursor-not-allowed disabled:opacity-50
            `}
            >
              <SelectValue className="flex items-center gap-2">
                {({ selectedItem }) => (
                  <>
                    {selectedItem && React.createElement(selectedItem.icon, { size: 14 })}
                    {selectedItem?.label || 'Select status'}
                  </>
                )}
              </SelectValue>
              <ChevronDown size={14} />
            </AriaButton>
            <Popover className={`
              mt-1 w-[var(--trigger-width)] rounded-md border border-gray-200
              bg-white shadow-lg
            `}
            >
              <ListBox className="max-h-60 overflow-auto p-1" items={statusOptions}>
                {item => (
                  <ListBoxItem
                    id={item.key}
                    textValue={item.label}
                    className={`
                      flex cursor-pointer items-center gap-2 rounded px-2 py-1.5
                      text-xs outline-none
                      hover:bg-gray-100
                      focus:bg-gray-100
                    `}
                  >
                    {React.createElement(item.icon, { size: 14 })}
                    {item.label}
                  </ListBoxItem>
                )}
              </ListBox>
            </Popover>
          </Select>
        </div>
      ),
    },
    {
      label: 'Review Tags',
      value: (
        <div className="flex flex-col items-end gap-2">
          {/* Add tags UI if completed and there are tag options */}
          { (
            <div className="flex flex-col items-end gap-2">
              <div className="flex flex-wrap justify-end gap-1">
                {qcTagOptions.map((tag) => {
                  const isSelected = pendingTags.has(tag.label)
                  let badgeColor = 'gray'
                  if (tag.type === 'success')
                    badgeColor = 'green'
                  else if (tag.type === 'failure')
                    badgeColor = 'red'
                  return (
                    <AriaButton
                      key={tag.label}
                      onPress={() => {
                        setPendingTags((prev) => {
                          const next = new Set(prev)
                          if (next.has(tag.label)) {
                            next.delete(tag.label)
                          }
                          else {
                            next.add(tag.label)
                          }
                          // Immediately update on click
                          updateStatus.mutate({ qc_tags: Array.from(next) })
                          return next
                        })
                      }}
                      isDisabled={updateStatus.isLoading}
                      className={`
                        m-0 border-none bg-transparent p-0 shadow-none
                        outline-none
                      `}
                      style={{ minWidth: 0 }}
                    >
                      <Badge
                        size="1"
                        color={badgeColor}
                        className={`
                          flex items-center gap-1 px-2 py-0.5 text-xs
                          font-medium transition
                          ${isSelected
                      ? (tag.type === 'success'
                          ? 'bg-green-100 text-green-800'
                          : tag.type === 'failure'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800')
                      : `
                        bg-white text-gray-600
                        hover:bg-gray-100
                      `}
                          border border-gray-200
                          ${isSelected ? '' : 'opacity-40'}
                        `}
                        style={{ minWidth: 0, height: 22, lineHeight: '18px', cursor: updateStatus.isLoading ? 'not-allowed' : 'pointer' }}
                      >
                        {tag.label}
                      </Badge>
                    </AriaButton>
                  )
                })}
              </div>
              {/* Save/Cancel buttons removed */}
            </div>
          )}
        </div>
      ),
    },
  ]

  const tags = parsePathwayTags(call.pathway_tags)

  return (
    <div className="flex w-full flex-col gap-0.5">
      <div className="grid grid-cols-1 gap-2">
        {callFields
          .filter(({ value }) => value !== null && value !== undefined)
          .map(({ label, value }) => (
            <span key={label} className="flex w-full justify-between">
              <p className={`
                text-xs font-semibold tracking-tight whitespace-nowrap
                text-gray-500
              `}
              >
                {label}
              </p>
              <p className="truncate text-xs text-gray-500 capitalize">
                {value}
              </p>
            </span>
          ))}
      </div>
      {tags?.length > 0 && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <p className={`
            pr-1.5 text-xs font-semibold tracking-tight text-gray-500
          `}
          >
            Pathway Tags:
          </p>
          {tags?.map(tag => (
            <Badge key={tag.name} size={1} color="gray" radius="large">
              <div
                style={{
                  height: 10,
                  width: 10,
                  borderRadius: '50%',
                  background: tag.color || '#000',
                }}
              />
              <p style={{ fontSize: 12, fontWeight: 500 }}>{tag.name}</p>
            </Badge>
          ))}
        </div>
      )}
      {call.error_message && (
        <div className={`
          mt-3 flex items-center gap-1.5 rounded border border-red-200 bg-red-50
          p-2
        `}
        >
          <p className="text-xs tracking-tight text-red-600">
            {call.error_message}
          </p>
        </div>
      )}
      {consecutiveWaitingTwice && (
        <div className={`
          mt-3 flex items-center gap-1.5 rounded border border-yellow-200
          bg-yellow-50 p-2
        `}
        >
          <AlertTriangle size={16} className="text-yellow-600" />
          <p className="text-xs tracking-tight text-yellow-600">
            Long periods of silence due to agent waiting. Update the prompt to specify when the agent should wait.
          </p>
        </div>
      )}
    </div>
  )
}
